import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Download, FileText, File, FileImage, Archive, Upload, X, CheckCircle2, Clock, FolderOpen, ArchiveX } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area,
} from 'recharts';
import { exportRecordPDF, exportRecordXLSX } from '@/lib/clientExport';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import TagChip from '@/components/ui/TagChip';
import CanAccess from '@/components/ui/CanAccess';
import { format } from 'date-fns';

interface Document {
  id: string;
  docCode: string;
  title: string;
  type: string;
  department: string;
  status: string;
  fileSize: number;
  fileFormat: string;
  version: string;
  uploadedAt: string;
  owner?: { name: string };
  tags: string[];
}

function fileSizeLabel(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function FileIcon({ type }: { type: string }) {
  const lower = (type || '').toLowerCase();
  if (lower.includes('pdf')) return <FileText size={16} className="text-red-500" />;
  if (lower.includes('image') || lower.includes('png') || lower.includes('jpg')) return <FileImage size={16} className="text-blue-500" />;
  return <File size={16} className="text-text-dim" />;
}

const MOCK_DOCUMENTS: Document[] = [
  {
    id: 'mock-doc-1', docCode: 'DOC-2025-001', title: 'HPLC Method Validation Protocol — Amoxicillin 500mg',
    type: 'Protocol', department: 'Quality Control', status: 'approved',
    fileSize: 1458432, fileFormat: 'pdf', version: '2.0',
    uploadedAt: '2025-02-15T10:30:00Z', owner: { name: 'Dr. Anjali Mehta' },
    tags: ['HPLC', 'validation', 'Amoxicillin', 'analytical'],
  },
  {
    id: 'mock-doc-2', docCode: 'DOC-2025-002', title: 'WHO GMP Compliance Certificate 2024-2027',
    type: 'Certificate', department: 'Quality Assurance', status: 'approved',
    fileSize: 524288, fileFormat: 'pdf', version: '1.0',
    uploadedAt: '2025-01-20T09:00:00Z', owner: { name: 'Priya Nair' },
    tags: ['WHO', 'GMP', 'certificate', 'compliance'],
  },
  {
    id: 'mock-doc-3', docCode: 'DOC-2025-003', title: 'Stability Study Report — Ciprofloxacin 250mg 6-Month',
    type: 'Report', department: 'R&D', status: 'under_review',
    fileSize: 3145728, fileFormat: 'pdf', version: '1.0',
    uploadedAt: '2025-03-28T14:00:00Z', owner: { name: 'Ramesh Kulkarni' },
    tags: ['stability', 'Ciprofloxacin', 'ICH', 'report'],
  },
  {
    id: 'mock-doc-4', docCode: 'DOC-2025-004', title: 'Equipment Qualification Master Plan — Sterile Suite',
    type: 'Manual', department: 'Engineering', status: 'draft',
    fileSize: 2097152, fileFormat: 'docx', version: '0.3',
    uploadedAt: '2025-04-05T11:15:00Z', owner: { name: 'Arun Menon' },
    tags: ['qualification', 'equipment', 'sterile', 'IQ/OQ/PQ'],
  },
  {
    id: 'mock-doc-5', docCode: 'DOC-2023-001', title: 'SOP-QC-001: Cleaning Validation of HPLC Columns',
    type: 'SOP', department: 'Quality Control', status: 'approved',
    fileSize: 872448, fileFormat: 'pdf', version: '3.0',
    uploadedAt: '2023-03-10T08:00:00Z', owner: { name: 'Dr. Anjali Mehta' },
    tags: ['SOP', 'cleaning', 'HPLC', 'column', 'validation'],
  },
  {
    id: 'mock-doc-6', docCode: 'DOC-2023-002', title: 'SOP-QA-002: Internal Audit Procedure',
    type: 'SOP', department: 'Quality Assurance', status: 'approved',
    fileSize: 655360, fileFormat: 'pdf', version: '2.1',
    uploadedAt: '2023-05-22T09:30:00Z', owner: { name: 'Priya Nair' },
    tags: ['SOP', 'audit', 'internal', 'QA'],
  },
  {
    id: 'mock-doc-7', docCode: 'DOC-2023-003', title: 'Annual Product Quality Review — Metformin 500mg 2022',
    type: 'Report', department: 'Quality Assurance', status: 'approved',
    fileSize: 4194304, fileFormat: 'pdf', version: '1.0',
    uploadedAt: '2023-06-30T15:00:00Z', owner: { name: 'Dr. Suresh Pillai' },
    tags: ['APQR', 'Metformin', 'annual', 'quality review'],
  },
  {
    id: 'mock-doc-8', docCode: 'DOC-2023-004', title: 'Dissolution Method Development Report — Atorvastatin 10mg',
    type: 'Report', department: 'R&D', status: 'archived',
    fileSize: 2621440, fileFormat: 'pdf', version: '1.0',
    uploadedAt: '2023-08-14T11:00:00Z', owner: { name: 'Ramesh Kulkarni' },
    tags: ['dissolution', 'Atorvastatin', 'method development', 'R&D'],
  },
  {
    id: 'mock-doc-9', docCode: 'DOC-2023-005', title: 'Environmental Monitoring SOP — Grade A/B Areas',
    type: 'SOP', department: 'Quality Control', status: 'approved',
    fileSize: 1048576, fileFormat: 'pdf', version: '4.0',
    uploadedAt: '2023-09-01T07:00:00Z', owner: { name: 'Dr. Anjali Mehta' },
    tags: ['SOP', 'environmental monitoring', 'cleanroom', 'Grade A', 'microbiology'],
  },
  {
    id: 'mock-doc-10', docCode: 'DOC-2023-006', title: 'Batch Manufacturing Record — Paracetamol 650mg Tablets',
    type: 'Form', department: 'Manufacturing', status: 'archived',
    fileSize: 1310720, fileFormat: 'xlsx', version: '2.0',
    uploadedAt: '2023-10-18T10:45:00Z', owner: { name: 'Vikram Desai' },
    tags: ['BMR', 'Paracetamol', 'manufacturing', 'batch record'],
  },
  {
    id: 'mock-doc-11', docCode: 'DOC-2024-001', title: 'Cleaning Validation Protocol — Reactor Vessels R-101 & R-102',
    type: 'Protocol', department: 'Quality Assurance', status: 'approved',
    fileSize: 1835008, fileFormat: 'pdf', version: '1.5',
    uploadedAt: '2024-01-12T09:00:00Z', owner: { name: 'Priya Nair' },
    tags: ['cleaning validation', 'reactor', 'API', 'protocol'],
  },
  {
    id: 'mock-doc-12', docCode: 'DOC-2024-002', title: 'Risk Assessment Report — Data Integrity Framework',
    type: 'Report', department: 'Quality Assurance', status: 'approved',
    fileSize: 3670016, fileFormat: 'pdf', version: '1.0',
    uploadedAt: '2024-02-28T14:30:00Z', owner: { name: 'Dr. Suresh Pillai' },
    tags: ['data integrity', 'risk assessment', 'ALCOA', 'compliance'],
  },
  {
    id: 'mock-doc-13', docCode: 'DOC-2024-003', title: 'SOP-MFG-015: In-Process Controls for Tablet Compression',
    type: 'SOP', department: 'Manufacturing', status: 'approved',
    fileSize: 786432, fileFormat: 'pdf', version: '3.2',
    uploadedAt: '2024-04-05T08:15:00Z', owner: { name: 'Vikram Desai' },
    tags: ['SOP', 'in-process controls', 'tablet', 'compression', 'manufacturing'],
  },
  {
    id: 'mock-doc-14', docCode: 'DOC-2024-004', title: 'Supplier Qualification Policy — API & Excipients',
    type: 'Policy', department: 'Regulatory Affairs', status: 'approved',
    fileSize: 524288, fileFormat: 'pdf', version: '2.0',
    uploadedAt: '2024-05-20T10:00:00Z', owner: { name: 'Nandini Verma' },
    tags: ['policy', 'supplier qualification', 'API', 'excipient', 'regulatory'],
  },
  {
    id: 'mock-doc-15', docCode: 'DOC-2024-005', title: 'NABL Accreditation Scope Document — LAB-001',
    type: 'Certificate', department: 'Quality Assurance', status: 'approved',
    fileSize: 614400, fileFormat: 'pdf', version: '1.0',
    uploadedAt: '2024-07-01T09:00:00Z', owner: { name: 'Priya Nair' },
    tags: ['NABL', 'accreditation', 'scope', 'certificate'],
  },
  {
    id: 'mock-doc-16', docCode: 'DOC-2024-006', title: 'Validation Master Plan 2024-2026',
    type: 'Manual', department: 'Quality Assurance', status: 'approved',
    fileSize: 2359296, fileFormat: 'pdf', version: '1.0',
    uploadedAt: '2024-08-15T11:30:00Z', owner: { name: 'Dr. Suresh Pillai' },
    tags: ['VMP', 'validation', 'master plan', 'QA'],
  },
  {
    id: 'mock-doc-17', docCode: 'DOC-2024-007', title: 'ICH Q3D Elemental Impurities Risk Assessment',
    type: 'Report', department: 'R&D', status: 'under_review',
    fileSize: 3145728, fileFormat: 'pdf', version: '0.9',
    uploadedAt: '2024-10-10T14:00:00Z', owner: { name: 'Ramesh Kulkarni' },
    tags: ['ICH Q3D', 'elemental impurities', 'risk assessment', 'R&D'],
  },
  {
    id: 'mock-doc-18', docCode: 'DOC-2024-008', title: 'Analytical Specification — Ciprofloxacin HCl API',
    type: 'Specification', department: 'Quality Control', status: 'approved',
    fileSize: 1048576, fileFormat: 'pdf', version: '4.5',
    uploadedAt: '2024-11-05T09:00:00Z', owner: { name: 'Dr. Anjali Mehta' },
    tags: ['specification', 'Ciprofloxacin', 'API', 'analytical'],
  },
  {
    id: 'mock-doc-19', docCode: 'DOC-2024-009', title: 'EHS Policy — Hazardous Chemical Handling',
    type: 'Policy', department: 'EHS', status: 'approved',
    fileSize: 720896, fileFormat: 'pdf', version: '1.5',
    uploadedAt: '2024-12-01T08:00:00Z', owner: { name: 'Arun Menon' },
    tags: ['EHS', 'policy', 'hazardous', 'chemical', 'safety'],
  },
  {
    id: 'mock-doc-20', docCode: 'DOC-2024-010', title: 'Batch Analytical Records — Atorvastatin 20mg Q3 2024',
    type: 'Form', department: 'Quality Control', status: 'archived',
    fileSize: 1572864, fileFormat: 'xlsx', version: '1.0',
    uploadedAt: '2024-12-20T16:00:00Z', owner: { name: 'Dr. Anjali Mehta' },
    tags: ['batch record', 'Atorvastatin', 'analytical', 'QC'],
  },
  {
    id: 'mock-doc-21', docCode: 'DOC-2025-005', title: 'SOP-REG-008: Change Control for Registered Products',
    type: 'SOP', department: 'Regulatory Affairs', status: 'approved',
    fileSize: 917504, fileFormat: 'pdf', version: '2.3',
    uploadedAt: '2025-01-08T09:00:00Z', owner: { name: 'Nandini Verma' },
    tags: ['SOP', 'change control', 'regulatory', 'variation'],
  },
  {
    id: 'mock-doc-22', docCode: 'DOC-2025-006', title: 'Process Validation Protocol — Omeprazole 20mg Capsules',
    type: 'Protocol', department: 'Manufacturing', status: 'under_review',
    fileSize: 2097152, fileFormat: 'pdf', version: '1.0',
    uploadedAt: '2025-02-28T10:00:00Z', owner: { name: 'Vikram Desai' },
    tags: ['process validation', 'Omeprazole', 'capsule', 'manufacturing'],
  },
  {
    id: 'mock-doc-23', docCode: 'DOC-2025-007', title: 'Analytical Specification — Metformin HCl Finished Product',
    type: 'Specification', department: 'Quality Control', status: 'under_review',
    fileSize: 819200, fileFormat: 'pdf', version: '3.1',
    uploadedAt: '2025-03-10T11:00:00Z', owner: { name: 'Dr. Anjali Mehta' },
    tags: ['specification', 'Metformin', 'finished product', 'QC'],
  },
  {
    id: 'mock-doc-24', docCode: 'DOC-2025-008', title: 'Technology Transfer Protocol — Amlodipine 5mg Tablets',
    type: 'Protocol', department: 'R&D', status: 'draft',
    fileSize: 1703936, fileFormat: 'docx', version: '0.5',
    uploadedAt: '2025-04-01T13:00:00Z', owner: { name: 'Ramesh Kulkarni' },
    tags: ['technology transfer', 'Amlodipine', 'protocol', 'R&D'],
  },
  {
    id: 'mock-doc-25', docCode: 'DOC-2026-001', title: 'Annual GMP Self-Inspection Report — 2025',
    type: 'Report', department: 'Quality Assurance', status: 'draft',
    fileSize: 2883584, fileFormat: 'pdf', version: '0.2',
    uploadedAt: '2026-01-30T10:00:00Z', owner: { name: 'Dr. Suresh Pillai' },
    tags: ['self-inspection', 'GMP', 'annual', 'audit', '2025'],
  },
];

const DOC_TYPES = ['SOP', 'Protocol', 'Report', 'Certificate', 'Form', 'Policy', 'Manual', 'Specification'];

export default function Dms() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    title: '', type: '', department: '', version: '1.0', ownerName: '', tags: '',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['documents', { search, type: typeFilter, status: statusFilter, page }],
    queryFn: () =>
      api.get('/documents', { params: { search, type: typeFilter, status: statusFilter, page, limit: 25 } })
        .then((r) => r.data),
  });

  const { data: analytics } = useQuery({
    queryKey: ['dms-analytics'],
    queryFn: () => api.get('/dms/analytics').then((r) => r.data.data),
    staleTime: 120000,
  });

  const { data: labs } = useQuery({
    queryKey: ['labs-all'],
    queryFn: () => api.get('/labs/all').then((r) => r.data.data),
  });

  const uploadMutation = useMutation({
    mutationFn: (fd: FormData) =>
      api.post('/documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setShowCreate(false);
      setSelectedFile(null);
      setUploadProgress(0);
      setUploadError('');
      setForm({ title: '', type: '', department: '', version: '1.0', ownerName: '', tags: '' });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Upload failed. Please try again.';
      setUploadError(msg);
      setUploadProgress(0);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/documents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setSelectedDoc(null);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (!['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)) {
        setUploadError('Only PDF and DOCX files are accepted');
        setSelectedFile(null);
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        setUploadError('File too large — maximum 50 MB');
        setSelectedFile(null);
        return;
      }
      setUploadError('');
      setSelectedFile(file);
      if (!form.title) setForm((f) => ({ ...f, title: file.name.replace(/\.[^.]+$/, '') }));
    }
  };

  const handleSubmit = () => {
    if (!selectedFile) { setUploadError('Please select a file'); return; }
    if (!form.title || !form.type || !form.department || !form.ownerName) {
      setUploadError('Title, Type, Department, and Owner are required');
      return;
    }
    setUploadError('');
    const fd = new FormData();
    fd.append('file', selectedFile);
    fd.append('title', form.title);
    fd.append('type', form.type);
    fd.append('department', form.department);
    fd.append('version', form.version);
    fd.append('ownerName', form.ownerName);
    fd.append('tags', form.tags);
    uploadMutation.mutate(fd);
  };

  const handleDownload = (doc: Document) => {
    const fields: [string, string | number][] = [
      ['Document ID', doc.docCode],
      ['Title', doc.title],
      ['Type', doc.type],
      ['Department', doc.department || '—'],
      ['Status', doc.status],
      ['Version', doc.version],
      ['File Format', (doc.fileFormat || '—').toUpperCase()],
      ['File Size', fileSizeLabel(doc.fileSize)],
      ['Uploaded', format(new Date(doc.uploadedAt), 'd MMM yyyy')],
      ['Owner', doc.owner?.name || '—'],
      ['Tags', (doc.tags || []).join(', ')],
    ];
    const fmt = (doc.fileFormat || '').toLowerCase();
    if (fmt === 'xlsx' || fmt === 'xls' || fmt === 'csv') {
      exportRecordXLSX(`${doc.docCode} - ${doc.title}`, fields);
    } else {
      exportRecordPDF(doc.title, doc.docCode, fields);
    }
  };

  const columns: Column<Record<string, unknown>>[] = [
    { key: 'docCode', label: 'Doc ID', width: 'w-28',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.docCode)}</span> },
    { key: 'title', label: 'Document',
      render: (row) => (
        <div className="flex items-start gap-2">
          <FileIcon type={String(row.fileFormat || '')} />
          <div>
            <p className="font-medium text-text text-sm leading-tight">{String(row.title)}</p>
            <p className="text-xs text-text-dim">{String(row.type)} · v{String(row.version)}</p>
          </div>
        </div>
      )},
    { key: 'department', label: 'Dept',
      render: (row) => <span className="text-sm text-text">{String(row.department || '—')}</span> },
    { key: 'status', label: 'Status', render: (row) => <PillBadge value={String(row.status)} showDot /> },
    { key: 'fileSize', label: 'Size', align: 'right',
      render: (row) => <span className="text-xs font-mono text-text-muted">{fileSizeLabel(Number(row.fileSize))}</span> },
    { key: 'uploadedAt', label: 'Uploaded',
      render: (row) => (
        <div>
          <p className="text-xs font-mono">{format(new Date(String(row.uploadedAt)), 'd MMM yyyy')}</p>
          <p className="text-[10px] text-text-dim">
            {(row.owner as { name: string } | null)?.name || '—'}
          </p>
        </div>
      )},
    { key: 'actions', label: '',
      render: (row) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => handleDownload(row as unknown as Document)}
            className="p-1.5 hover:bg-bg rounded-lg transition-colors text-text-dim hover:text-teal">
            <Download size={12} />
          </button>
        </div>
      )},
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Document Management"
        subtitle={`${data?.meta?.total || 0} documents`}
        actions={
          <CanAccess roles={['admin', 'qa_director', 'lab_head', 'qc_analyst']}>
            <button onClick={() => setShowCreate(true)} className="btn-primary"><Upload size={13} /> Upload Document</button>
          </CanAccess>
        }
      />

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input placeholder="Search documents..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="input pl-8" />
        </div>
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Types</option>
          {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Status</option>
          <option value="approved">Approved</option>
          <option value="draft">Draft</option>
          <option value="under_review">Under Review</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* ── DMS Analytics ─────────────────────────────────────────── */}
      {analytics && (
        <>
          {/* Stat strip */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Total Documents', value: analytics.total,       color: '#2563EB', icon: <FolderOpen  size={16} /> },
              { label: 'Approved',        value: analytics.approved,    color: '#16A34A', icon: <CheckCircle2 size={16} /> },
              { label: 'Under Review',    value: analytics.underReview, color: '#D97706', icon: <Clock        size={16} /> },
              { label: 'Archived',        value: analytics.archived,    color: '#6B7280', icon: <ArchiveX     size={16} /> },
            ].map((s) => (
              <div key={s.label} className="card p-4 flex items-center gap-3" style={{ borderLeft: `3px solid ${s.color}` }}>
                <div style={{ color: s.color, flexShrink: 0 }}>{s.icon}</div>
                <div>
                  <p className="text-xs text-text-muted mb-0.5">{s.label}</p>
                  <p className="text-xl font-bold font-mono" style={{ color: s.color }}>{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Document Type Breakdown */}
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-text mb-1">Documents by Type</h3>
              <p className="text-xs text-text-muted mb-3">Count per document category</p>
              <div style={{ height: 200 }}>
                <ResponsiveContainer>
                  <BarChart data={analytics.byType} margin={{ top: 4, right: 8, bottom: 4, left: -12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="type" tick={{ fontSize: 10, fill: '#5a5a6e' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Bar dataKey="count" name="Documents" fill="#2563EB" radius={[3, 3, 0, 0]} maxBarSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Upload Activity Trend */}
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-text mb-1">Upload Activity</h3>
              <p className="text-xs text-text-muted mb-3">Documents uploaded per month (12M)</p>
              <div style={{ height: 200 }}>
                <ResponsiveContainer>
                  <AreaChart data={analytics.uploadTrend} margin={{ top: 4, right: 8, bottom: 4, left: -12 }}>
                    <defs>
                      <linearGradient id="uploadGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#b07d1a" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#b07d1a" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="label" tick={{ fontSize: 9.5, fill: '#5a5a6e' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Area dataKey="count" name="Uploads" stroke="#b07d1a" strokeWidth={2} fill="url(#uploadGrad)" dot={{ r: 3, fill: '#b07d1a', strokeWidth: 0 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}

      <DataTable
        columns={columns}
        data={((data?.data && data.data.length > 0) ? data.data : MOCK_DOCUMENTS) as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedDoc(row as unknown as Document)}
        total={data?.meta?.total}
        page={page}
        pageSize={25}
        onPageChange={setPage}
      />

      {/* Detail Modal */}
      <Modal isOpen={!!selectedDoc} onClose={() => setSelectedDoc(null)}
        title={selectedDoc?.title || ''} subtitle={selectedDoc?.docCode} size="md"
        footer={
          <div className="flex justify-between w-full">
            <CanAccess roles={['admin', 'qa_director']}>
              <button
                onClick={() => selectedDoc && archiveMutation.mutate(selectedDoc.id)}
                disabled={archiveMutation.isPending}
                className="btn-ghost text-risk-high hover:bg-risk-high/10"
              >
                <Archive size={13} /> Archive
              </button>
            </CanAccess>
            <div className="flex gap-2">
              <button onClick={() => setSelectedDoc(null)} className="btn-secondary">Close</button>
              {selectedDoc && (
                <button onClick={() => handleDownload(selectedDoc)} className="btn-primary">
                  <Download size={13} /> Download
                </button>
              )}
            </div>
          </div>
        }
      >
        {selectedDoc && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <FileIcon type={selectedDoc.fileFormat} />
              <PillBadge value={selectedDoc.status} showDot />
              <span className="font-mono text-xs bg-teal/10 text-teal px-2 py-0.5 rounded ml-auto">v{selectedDoc.version}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Type', value: selectedDoc.type },
                { label: 'Department', value: selectedDoc.department || '—' },
                { label: 'File Format', value: (selectedDoc.fileFormat || '—').toUpperCase() },
                { label: 'File Size', value: fileSizeLabel(selectedDoc.fileSize) },
                { label: 'Uploaded', value: format(new Date(selectedDoc.uploadedAt), 'd MMM yyyy') },
                { label: 'By', value: selectedDoc.owner?.name || '—' },
              ].map((item) => (
                <div key={item.label} className="p-3 bg-bg rounded-lg">
                  <p className="text-xs text-text-muted mb-0.5">{item.label}</p>
                  <p className="text-sm font-medium text-text">{item.value}</p>
                </div>
              ))}
            </div>
            {selectedDoc.tags && selectedDoc.tags.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-2">Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedDoc.tags.map((t) => <TagChip key={t} label={t} color="gray" />)}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Upload Modal */}
      <Modal isOpen={showCreate} onClose={() => { setShowCreate(false); setSelectedFile(null); setUploadProgress(0); setUploadError(''); }}
        title="Upload Document" subtitle="PDF or DOCX, max 50 MB" size="lg"
        footer={
          <>
            <button onClick={() => { setShowCreate(false); setSelectedFile(null); setUploadProgress(0); setUploadError(''); }} className="btn-secondary">Cancel</button>
            <button onClick={handleSubmit} disabled={uploadMutation.isPending} className="btn-primary">
              {uploadMutation.isPending ? `Uploading${uploadProgress ? ` ${uploadProgress}%` : '...'}` : 'Upload'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {/* File Picker */}
          <div>
            <label className="label">File *</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                ${selectedFile ? 'border-teal bg-teal/5' : 'border-border hover:border-teal/50'}`}
            >
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText size={20} className="text-teal" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-text">{selectedFile.name}</p>
                    <p className="text-xs text-text-muted">{fileSizeLabel(selectedFile.size)}</p>
                  </div>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="ml-auto p-1 hover:bg-bg rounded">
                    <X size={14} className="text-text-dim" />
                  </button>
                </div>
              ) : (
                <div>
                  <Upload size={24} className="mx-auto text-text-dim mb-2" />
                  <p className="text-sm text-text">Click to select a file</p>
                  <p className="text-xs text-text-muted mt-1">PDF or DOCX only · max 50 MB</p>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept=".pdf,.docx" className="hidden" onChange={handleFileChange} />
          </div>

          {/* Upload Progress */}
          {uploadMutation.isPending && uploadProgress > 0 && (
            <div>
              <div className="flex justify-between text-xs text-text-muted mb-1">
                <span>Uploading to secure storage...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-border rounded-full overflow-hidden">
                <div className="h-full bg-teal transition-all rounded-full" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          {uploadError && (
            <p className="text-xs text-risk-high bg-risk-high/10 px-3 py-2 rounded-lg">{uploadError}</p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label">Document Title *</label>
              <input className="input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. HPLC Method Validation Protocol" />
            </div>
            <div>
              <label className="label">Type *</label>
              <select className="select" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                <option value="">Select...</option>
                {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Department *</label>
              <input className="input" value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} placeholder="e.g. QC" />
            </div>
            <div>
              <label className="label">Owner Name *</label>
              <input className="input" value={form.ownerName} onChange={(e) => setForm((f) => ({ ...f, ownerName: e.target.value }))} placeholder="e.g. Dr. Priya Nair" />
            </div>
            <div>
              <label className="label">Version</label>
              <input className="input" value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} placeholder="1.0" />
            </div>
            <div className="col-span-2">
              <label className="label">Tags (comma-separated)</label>
              <input className="input" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="e.g. GMP, validation, HPLC" />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
